import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../api/firebase';

/**
 * PlanContext
 * 사용자의 구독 상태(entitlements)를 실시간으로 관리하는 Context
 * 
 * entitlements 컬렉션 구조:
 * {
 *   userId: string,
 *   plan: 'basic' | 'pro',
 *   active: boolean,
 *   status: 'none' | 'active' | 'canceled' | 'payment_failed' | 'past_due',
 *   subscriptionId?: string,
 *   expiresAt?: Timestamp,
 *   needsAction?: boolean,
 *   updatedAt: Timestamp
 * }
 */

const PlanContext = createContext({
  ent: { plan: 'basic', active: false, status: 'none' },
  loading: true,
  isPro: false,
  needsPaymentAction: false
});

export function PlanProvider({ children }) {
  const { currentUser } = useAuth();
  const [ent, setEnt] = useState({ 
    plan: 'basic', 
    active: false, 
    status: 'none' 
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 로그인하지 않은 경우
    if (!currentUser) {
      setEnt({ plan: 'basic', active: false, status: 'none' });
      setLoading(false);
      return;
    }

    // Firestore 실시간 구독 설정
    // (default) DB의 entitlements 컬렉션에서 사용자 문서 구독
    const entRef = doc(db, 'entitlements', currentUser.uid);
    
    const unsub = onSnapshot(
      entRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setEnt({
            plan: data.plan || 'basic',
            active: data.active || false,
            status: data.status || 'none',
            subscriptionId: data.subscriptionId || null,
            expiresAt: data.expiresAt || null,
            needsAction: data.needsAction || false,
            updatedAt: data.updatedAt || null
          });
        } else {
          // 문서가 없으면 기본값 (무료 사용자)
          setEnt({ plan: 'basic', active: false, status: 'none' });
        }
        setLoading(false);
      },
      (error) => {
        console.error('entitlements 구독 오류:', error);
        // 오류 발생 시 기본값으로 설정
        setEnt({ plan: 'basic', active: false, status: 'none' });
        setLoading(false);
      }
    );

    // cleanup: 컴포넌트 언마운트 시 구독 해제
    return () => unsub();
  }, [currentUser]);

  // 파생된 상태값들을 계산
  const isPro = ent?.active === true && ent?.plan === 'pro';
  const needsPaymentAction = 
    ent?.needsAction === true || 
    ent?.status === 'payment_failed' || 
    ent?.status === 'past_due';

  return (
    <PlanContext.Provider value={{ 
      ent, 
      loading, 
      isPro, 
      needsPaymentAction 
    }}>
      {children}
    </PlanContext.Provider>
  );
}

/**
 * usePlan Hook
 * 컴포넌트에서 구독 상태를 사용하기 위한 Hook
 * 
 * @returns {Object} { ent, loading, isPro, needsPaymentAction }
 * 
 * @example
 * function MyComponent() {
 *   const { isPro, loading } = usePlan();
 *   
 *   if (loading) return <div>로딩 중...</div>;
 *   if (!isPro) return <div>프로 회원 전용 기능입니다.</div>;
 *   
 *   return <div>프로 기능 사용 가능!</div>;
 * }
 */
export function usePlan() {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error('usePlan must be used within a PlanProvider');
  }
  return context;
}

/**
 * 헬퍼 함수: 프로 회원 여부 확인
 */
export function isPro(ent) {
  return ent?.active === true && ent?.plan === 'pro';
}

/**
 * 헬퍼 함수: 결제 액션이 필요한지 확인
 */
export function needsPaymentAction(ent) {
  return ent?.needsAction === true || 
         ent?.status === 'payment_failed' || 
         ent?.status === 'past_due';
}

/**
 * 헬퍼 함수: 플랜 상태 텍스트 반환
 */
export function getPlanStatusText(ent) {
  if (!ent) return '알 수 없음';
  
  const statusMap = {
    'active': '활성',
    'canceled': '해지됨',
    'payment_failed': '결제 실패',
    'past_due': '결제 지연',
    'none': '미가입'
  };
  
  return statusMap[ent.status] || ent.status;
}

/**
 * 헬퍼 함수: 플랜명 반환
 */
export function getPlanName(ent) {
  if (!ent) return 'Basic';
  return ent.plan === 'pro' ? 'PRO' : 'Basic';
}

